import { environment } from "@app/relay/relay-environment";
import { useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type { categoryTree_CreateCategoryMutation } from "@app/queries/categoryTree_CreateCategoryMutation.graphql";
import type { categoryTree_UpdateCategoryMutation } from "@app/queries/categoryTree_UpdateCategoryMutation.graphql";
import type { categoryTree_MoveCategoryMutation } from "@app/queries/categoryTree_MoveCategoryMutation.graphql";
import type { categoryTree_DeleteCategoryMutation } from "@app/queries/categoryTree_DeleteCategoryMutation.graphql";

import { STAKEHOLDER_DRAG_TYPE } from "./categorize-page";
import type { Category, PoolEntry } from "./types";

const createCategoryMutation = graphql`
  mutation categoryTree_CreateCategoryMutation(
    $input: CreateCategoryInputObject!
  ) {
    createCategory(input: $input) {
      category {
        id
      }
      errors {
        message
      }
    }
  }
`;

const updateCategoryMutation = graphql`
  mutation categoryTree_UpdateCategoryMutation(
    $input: UpdateCategoryInputObject!
  ) {
    updateCategory(input: $input) {
      category {
        id
      }
      errors {
        message
      }
    }
  }
`;

const moveCategoryMutation = graphql`
  mutation categoryTree_MoveCategoryMutation($input: MoveCategoryInputObject!) {
    moveCategory(input: $input) {
      category {
        id
      }
      errors {
        message
      }
    }
  }
`;

const deleteCategoryMutation = graphql`
  mutation categoryTree_DeleteCategoryMutation(
    $input: DeleteCategoryInputObject!
  ) {
    deleteCategory(input: $input) {
      category {
        id
      }
      errors {
        message
      }
    }
  }
`;

const CATEGORY_DRAG_TYPE = "application/x-cxcash-category";
const MAX_DEPTH = 3;

const PNL_LINES = [
  "COGS",
  "S_AND_M",
  "R_AND_D",
  "G_AND_A",
  "TAXES",
  "EXCLUDED",
];

// A fresh node gets a readable color so the board stays scannable
const PALETTE = [
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#f43f5e",
  "#14b8a6",
  "#f97316",
];

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export default function CategoryTree({
  perspectiveId,
  categories,
  stakeholders,
  onChanged,
  onDropStakeholders,
  onOpenStakeholder,
  onOpenCategory,
}: {
  perspectiveId: string;
  categories: readonly Category[];
  stakeholders: readonly PoolEntry[];
  onChanged: () => void;
  onDropStakeholders: (categoryId: string, stakeholderIds: string[]) => void;
  onOpenStakeholder: (entry: PoolEntry) => void;
  onOpenCategory: (category: Category) => void;
}) {
  const byParent = useMemo(() => {
    const map = new Map<string | null, Category[]>();
    for (const category of categories) {
      const key = category.parent?.id ?? null;
      const siblings = map.get(key) ?? [];
      siblings.push(category);
      map.set(key, siblings);
    }
    for (const siblings of map.values())
      siblings.sort((a, b) => a.rank - b.rank);
    return map;
  }, [categories]);

  // Depth (top-level = 1) and subtree height (leaf = 1) per node, used to keep
  // nesting within MAX_DEPTH levels when dragging.
  const { depthOf, heightOf, subtreeIdsOf } = useMemo(() => {
    const childrenOf = (id: string | null) => byParent.get(id) ?? [];

    const depthOf = new Map<string, number>();
    const assignDepth = (id: string | null, depth: number) => {
      for (const child of childrenOf(id)) {
        depthOf.set(child.id, depth);
        assignDepth(child.id, depth + 1);
      }
    };
    assignDepth(null, 1);

    const heightOf = new Map<string, number>();
    const computeHeight = (id: string): number => {
      const children = childrenOf(id);
      const height =
        children.length === 0
          ? 1
          : 1 + Math.max(...children.map((c) => computeHeight(c.id)));
      heightOf.set(id, height);
      return height;
    };
    const subtreeIdsOf = new Map<string, Set<string>>();
    const collect = (id: string): Set<string> => {
      const ids = new Set<string>([id]);
      for (const child of childrenOf(id)) {
        for (const descendant of collect(child.id)) ids.add(descendant);
      }
      subtreeIdsOf.set(id, ids);
      return ids;
    };
    for (const root of childrenOf(null)) {
      computeHeight(root.id);
      collect(root.id);
    }
    return { depthOf, heightOf, subtreeIdsOf };
  }, [byParent]);

  // A node may nest under a target when it isn't the target or one of its own
  // descendants, and the move keeps the whole subtree within MAX_DEPTH.
  const canNest = (draggedId: string | null, targetId: string) => {
    if (!draggedId || draggedId === targetId) return false;
    if (subtreeIdsOf.get(draggedId)?.has(targetId)) return false;
    return (
      (depthOf.get(targetId) ?? 1) + (heightOf.get(draggedId) ?? 1) <= MAX_DEPTH
    );
  };

  const [draggingId, setDraggingId] = useState<string | null>(null);

  // The counterparties allocated directly to each node (with the dollars that
  // allocation carries) and the rolled-up total for each node including all of
  // its descendants. This is what the expanded node reveals.
  const { directStakeholders, mappedCounts, rolledTotal } = useMemo(() => {
    const directStakeholders = new Map<
      string,
      { entry: PoolEntry; amount: number }[]
    >();
    for (const entry of stakeholders) {
      for (const allocation of entry.allocations) {
        const list = directStakeholders.get(allocation.category.id) ?? [];
        list.push({ entry, amount: entry.totalSpend * allocation.weight });
        directStakeholders.set(allocation.category.id, list);
      }
    }
    for (const list of directStakeholders.values()) {
      list.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    }

    const mappedCounts = new Map<string, number>();
    for (const [categoryId, list] of directStakeholders)
      mappedCounts.set(categoryId, list.length);

    const ownTotal = (id: string) =>
      (directStakeholders.get(id) ?? []).reduce(
        (sum, item) => sum + item.amount,
        0
      );
    const rolledTotal = new Map<string, number>();
    const computeRolled = (id: string): number => {
      let total = ownTotal(id);
      for (const child of byParent.get(id) ?? [])
        total += computeRolled(child.id);
      rolledTotal.set(id, total);
      return total;
    };
    for (const root of byParent.get(null) ?? []) computeRolled(root.id);

    return { directStakeholders, mappedCounts, rolledTotal };
  }, [stakeholders, byParent]);

  // Top-level nodes start open so the board is legible at a glance; deeper
  // levels and stakeholder lists are revealed on demand via the carets.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set((byParent.get(null) ?? []).map((category) => category.id))
  );
  const toggleExpanded = (id: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Nodes worth opening in "expand all" — those that actually reveal something
  // (subcategories or allocated counterparties).
  const expandableIds = useMemo(
    () =>
      categories
        .filter(
          (category) =>
            (byParent.get(category.id)?.length ?? 0) > 0 ||
            (directStakeholders.get(category.id)?.length ?? 0) > 0
        )
        .map((category) => category.id),
    [categories, byParent, directStakeholders]
  );
  const anyExpanded = expandableIds.some((id) => expanded.has(id));

  const fail = (error: Error) => window.alert(error.message);
  const reportErrors = (
    _response: unknown,
    errors: ReadonlyArray<{ readonly message: string }> | null | undefined
  ) => (errors?.length ? window.alert(errors[0].message) : onChanged());

  const createCategory = (parentId: string | null) => {
    const name = window.prompt(
      parentId ? "Name the new subcategory:" : "Name the new category:"
    );
    if (!name?.trim()) return;

    commitMutation<categoryTree_CreateCategoryMutation>(environment, {
      mutation: createCategoryMutation,
      variables: {
        input: {
          perspectiveId,
          parentId,
          name: name.trim(),
          color: PALETTE[categories.length % PALETTE.length],
        },
      },
      onCompleted: reportErrors,
      onError: fail,
    });
  };

  const renameCategory = (category: Category) => {
    const name = window.prompt("Rename category:", category.name);
    if (!name?.trim() || name.trim() === category.name) return;

    commitMutation<categoryTree_UpdateCategoryMutation>(environment, {
      mutation: updateCategoryMutation,
      variables: { input: { categoryId: category.id, name: name.trim() } },
      onCompleted: reportErrors,
      onError: fail,
    });
  };

  const setPnlLine = (category: Category, pnlLine: string) => {
    commitMutation<categoryTree_UpdateCategoryMutation>(environment, {
      mutation: updateCategoryMutation,
      variables: { input: { categoryId: category.id, pnlLine } },
      onCompleted: onChanged,
      onError: fail,
    });
  };

  const moveCategory = (categoryId: string, parentId: string | null) => {
    commitMutation<categoryTree_MoveCategoryMutation>(environment, {
      mutation: moveCategoryMutation,
      variables: { input: { categoryId, parentId } },
      onCompleted: reportErrors,
      onError: fail,
    });
  };

  const deleteCategory = (category: Category) => {
    const mapped = mappedCounts.get(category.id) ?? 0;
    const children = byParent.get(category.id)?.length ?? 0;
    const warning = [
      `Delete "${category.name}"?`,
      children > 0 ? `Its ${children} subcategories move up one level.` : null,
      mapped > 0 ? `${mapped} counterparties lose this allocation.` : null,
    ]
      .filter(Boolean)
      .join(" ");
    if (!window.confirm(warning)) return;

    commitMutation<categoryTree_DeleteCategoryMutation>(environment, {
      mutation: deleteCategoryMutation,
      variables: { input: { categoryId: category.id } },
      onCompleted: reportErrors,
      onError: fail,
    });
  };

  // Dropping counterparties onto a node should reveal them landing there —
  // otherwise a categorized counterparty just vanishes from the pool.
  const dropStakeholders = (categoryId: string, stakeholderIds: string[]) => {
    setExpanded((previous) => new Set(previous).add(categoryId));
    onDropStakeholders(categoryId, stakeholderIds);
  };

  const tree: TreeHandlers = {
    byParent,
    depthOf,
    mappedCounts,
    directStakeholders,
    rolledTotal,
    expanded,
    toggleExpanded,
    draggingId,
    canNest,
    setDraggingId,
    createCategory,
    renameCategory,
    deleteCategory,
    setPnlLine,
    onDropStakeholders: dropStakeholders,
    onMoveCategory: moveCategory,
    onOpenStakeholder,
    onOpenCategory,
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg"
      // The board surface is the unnest target: dropping a category here makes
      // it top-level. Stakeholder drags must land on a node.
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes(CATEGORY_DRAG_TYPE))
          event.preventDefault();
      }}
      onDrop={(event) => {
        const categoryId = event.dataTransfer.getData(CATEGORY_DRAG_TYPE);
        if (categoryId) {
          event.preventDefault();
          if ((depthOf.get(categoryId) ?? 1) !== 1)
            moveCategory(categoryId, null);
        }
      }}
    >
      <div className="p-3 border-b border-gray-100 flex items-center">
        <span className="text-sm font-semibold text-gray-700">
          Cost centers
        </span>
        <button
          type="button"
          onClick={() =>
            setExpanded(anyExpanded ? new Set() : new Set(expandableIds))
          }
          className="ml-2 text-xs text-gray-500 hover:text-gray-800"
        >
          {anyExpanded ? "Collapse all" : "Expand all"}
        </button>
        <span className="ml-2 text-xs text-gray-400">
          drop counterparties on a node — drag nodes to nest (up to {MAX_DEPTH}{" "}
          levels) or onto the panel to unnest
        </span>
        <button
          type="button"
          onClick={() => createCategory(null)}
          className="ml-auto text-sm text-emerald-600 hover:text-emerald-800"
        >
          + Add category
        </button>
      </div>

      <div className="p-3 grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
        {(byParent.get(null) ?? []).map((top) => (
          <div
            key={top.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <CategoryNode category={top} depth={1} tree={tree} />
          </div>
        ))}
      </div>
    </div>
  );
}

type TreeHandlers = {
  byParent: Map<string | null, Category[]>;
  depthOf: Map<string, number>;
  mappedCounts: Map<string, number>;
  directStakeholders: Map<string, { entry: PoolEntry; amount: number }[]>;
  rolledTotal: Map<string, number>;
  expanded: ReadonlySet<string>;
  toggleExpanded: (id: string) => void;
  draggingId: string | null;
  canNest: (draggedId: string | null, targetId: string) => boolean;
  setDraggingId: (id: string | null) => void;
  createCategory: (parentId: string | null) => void;
  renameCategory: (category: Category) => void;
  deleteCategory: (category: Category) => void;
  setPnlLine: (category: Category, pnlLine: string) => void;
  onDropStakeholders: (categoryId: string, stakeholderIds: string[]) => void;
  onMoveCategory: (categoryId: string, parentId: string | null) => void;
  onOpenStakeholder: (entry: PoolEntry) => void;
  onOpenCategory: (category: Category) => void;
};

function CategoryNode({
  category,
  depth,
  tree,
}: {
  category: Category;
  depth: number;
  tree: TreeHandlers;
}) {
  const children = tree.byParent.get(category.id) ?? [];
  const directStakeholders = tree.directStakeholders.get(category.id) ?? [];
  const isTop = depth === 1;
  const expanded = tree.expanded.has(category.id);

  // A node is worth expanding when it holds anything, or when a subcategory can
  // still be added under it.
  const expandable =
    children.length > 0 || directStakeholders.length > 0 || depth < MAX_DEPTH;

  return (
    <div className={isTop ? "" : "border-l border-gray-200 ml-3"}>
      <NodeRow
        category={category}
        depth={depth}
        tree={tree}
        expanded={expanded}
        expandable={expandable}
      />

      {expanded && (
        <div
          className={
            isTop
              ? "px-2 py-1.5 flex flex-col gap-0.5 bg-gray-50/50"
              : "pl-2 flex flex-col gap-0.5"
          }
        >
          {children.map((child) => (
            <CategoryNode
              key={child.id}
              category={child}
              depth={depth + 1}
              tree={tree}
            />
          ))}

          {directStakeholders.map(({ entry, amount }) => (
            <div
              key={entry.stakeholder.id}
              draggable
              // Drag onto another node to re-categorize, or back onto the pool to
              // pull a miscategorized counterparty out entirely.
              onDragStart={(event) => {
                event.stopPropagation();
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData(
                  STAKEHOLDER_DRAG_TYPE,
                  JSON.stringify([entry.stakeholder.id])
                );
              }}
              className="flex items-center gap-2 px-2 py-1 pl-6 text-xs text-gray-500 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded"
            >
              <button
                type="button"
                onClick={() => tree.onOpenStakeholder(entry)}
                title="Open details"
                className="truncate flex-1 text-left hover:underline"
              >
                {entry.stakeholder.name}
              </button>
              <span className="shrink-0 tabular-nums text-gray-400">
                {money(amount)}
              </span>
            </div>
          ))}

          {depth < MAX_DEPTH && (
            <button
              type="button"
              onClick={() => tree.createCategory(category.id)}
              className="text-left text-xs text-gray-400 hover:text-emerald-700 px-2 py-1 pl-6"
            >
              + subcategory
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NodeRow({
  category,
  depth,
  tree,
  expanded,
  expandable,
}: {
  category: Category;
  depth: number;
  tree: TreeHandlers;
  expanded: boolean;
  expandable: boolean;
}) {
  const [dropState, setDropState] = useState<"none" | "ok" | "blocked">("none");
  const isTop = depth === 1;
  const total = tree.rolledTotal.get(category.id) ?? 0;

  return (
    <div
      draggable
      onDragStart={(event) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(CATEGORY_DRAG_TYPE, category.id);
        tree.setDraggingId(category.id);
      }}
      onDragEnd={() => tree.setDraggingId(null)}
      onDragOver={(event) => {
        const types = event.dataTransfer.types;
        if (types.includes(STAKEHOLDER_DRAG_TYPE)) {
          event.preventDefault();
          event.stopPropagation();
          setDropState("ok");
        } else if (types.includes(CATEGORY_DRAG_TYPE)) {
          event.preventDefault();
          event.stopPropagation();
          setDropState(
            tree.canNest(tree.draggingId, category.id) ? "ok" : "blocked"
          );
        }
      }}
      onDragLeave={() => setDropState("none")}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setDropState("none");

        const stakeholderPayload = event.dataTransfer.getData(
          STAKEHOLDER_DRAG_TYPE
        );
        if (stakeholderPayload) {
          tree.onDropStakeholders(category.id, JSON.parse(stakeholderPayload));
          return;
        }

        const draggedCategoryId =
          event.dataTransfer.getData(CATEGORY_DRAG_TYPE);
        if (draggedCategoryId && tree.canNest(draggedCategoryId, category.id)) {
          tree.onMoveCategory(draggedCategoryId, category.id);
        }
      }}
      className={`group flex items-center gap-2 px-2 rounded cursor-grab active:cursor-grabbing transition-colors ${
        isTop ? "py-2 bg-white" : "py-1"
      } ${
        dropState === "ok"
          ? "bg-emerald-50 ring-2 ring-emerald-400"
          : dropState === "blocked"
          ? "ring-2 ring-red-300 cursor-no-drop"
          : "hover:bg-gray-100"
      }`}
    >
      {expandable ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            tree.toggleExpanded(category.id);
          }}
          title={expanded ? "Collapse" : "Expand"}
          className="shrink-0 w-4 text-gray-400 hover:text-gray-700 text-xs"
        >
          {expanded ? "▾" : "▸"}
        </button>
      ) : (
        <span className="shrink-0 w-4" />
      )}
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: category.color }}
      />
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          tree.onOpenCategory(category);
        }}
        title="Open category details"
        className={`truncate text-left hover:underline ${
          isTop
            ? "text-sm font-semibold text-gray-800"
            : "text-sm text-gray-600"
        }`}
      >
        {category.name}
      </button>
      {(tree.mappedCounts.get(category.id) ?? 0) > 0 && (
        <span className="text-[10px] px-1.5 rounded-full bg-gray-200 text-gray-600 tabular-nums">
          {tree.mappedCounts.get(category.id)}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2">
        <span
          className={`tabular-nums shrink-0 ${
            isTop
              ? "text-sm font-semibold text-gray-700"
              : "text-xs text-gray-500"
          }`}
        >
          {money(total)}
        </span>
        <span className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100">
          {isTop && (
            <select
              value={category.pnlLine ?? ""}
              onChange={(event) =>
                tree.setPnlLine(category, event.target.value)
              }
              onClick={(event) => event.stopPropagation()}
              title="P&L line"
              className="text-[10px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-500"
            >
              <option value="" disabled>
                P&L line…
              </option>
              {PNL_LINES.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => tree.renameCategory(category)}
            title="Rename"
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            rename
          </button>
          <button
            type="button"
            onClick={() => tree.deleteCategory(category)}
            title="Delete"
            className="text-xs text-red-300 hover:text-red-600"
          >
            delete
          </button>
        </span>
      </span>
    </div>
  );
}
